import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { Picture } from "@/components/wpl/image/src/index.ts";
import { PricingFeatureTable } from "@/components/wpl/pricing/src/features-table";
import { usePricingContentContext } from "@/components/wpl/pricing/src/pricing-content-context.tsx";
import {
    PricingFaq,
    QuotationsAdornment
} from "@/components/wpl/pricing/src/pricing-faq/index.ts";
import {
    PricingPageLayout,
    PageHeading
} from "@/components/wpl/pricing/src/pricing-page-layout";
import { GlobalNav } from "@/patterns/global-nav/src/nav-global.tsx";
import { CTA_URL_PATH } from "@/templates/collections/strategy/pricing/sections/urls.ts";
import {
    CallToAction,
    PricingContent
} from "@/utils/pricing-queries/get-pricing-content-gql.ts";
import { ParentScrollTracking } from "@/utils/scroll-tracking/index.ts";
import { UserLocale } from "@/utils/useGeoCurrency.ts";

import { AppPricingData } from "../../../../contentful/index.tsx";
import { CollectionsCompoundSubNavProps } from "../../collections-compound-nav-props.tsx";

import { StrategyCollectionPriceFinder } from "./sections/strategy-collection-price-finder.tsx";

export interface StrategyCollectionPricingPageTemplateProps {
    pricing?: AppPricingData;
    countryCode?: UserLocale;
    pricingContent?: PricingContent;
}

// eslint-disable-next-line complexity
export const StrategyCollectionPricingPageTemplate = (
    props: StrategyCollectionPricingPageTemplateProps
) => {
    const intl = useIntl();

    const content = usePricingContentContext();

    const featuresCtaContent = content
        ?.callToActionContainer?.[1] as CallToAction;
    const questionsCtaContent = content
        ?.callToActionContainer?.[0] as CallToAction;

    const strategyCollectionsPricingNav = (
        <GlobalNav
            productKey={"home"}
            compoundNav={{
                ...CollectionsCompoundSubNavProps(),
                ctaUrl: CTA_URL_PATH
            }}
        />
    );

    return (
        <PricingPageLayout
            nav={strategyCollectionsPricingNav}
            showFallback={content?.hasError}
        >
            <ParentScrollTracking
                scrollTrackingEnabled={true}
                pageId="strategy-collection-pricing-page"
            >
                <div css={headingContainerCss}>
                    <Picture
                        image={{
                            src: "https://dam-cdn.atl.orangelogic.com/AssetLink/5j2mvlle35t45d0e48ye5tqpoe0m63gu.svg",
                            alt: intl.formatMessage({
                                id: "StrategyCollectionPricingPageTemplate.headingLogoAlt",
                                defaultMessage: "Strategy Collection logo"
                            })
                        }}
                    />
                    <PageHeading>{content?.headline}</PageHeading>
                </div>

                <StrategyCollectionPriceFinder
                    pricing={props.pricing}
                    countryCode={props.countryCode}
                />
                <PricingFeatureTable
                    title={content?.compareFeaturesTitle ?? undefined}
                    comparePlans={content?.comparePlans ?? undefined}
                    compareFeatures={content?.compareFeatures ?? undefined}
                    linkValues={{
                        linkText: featuresCtaContent?.text ?? undefined,
                        linkHref: featuresCtaContent?.url ?? undefined
                    }}
                    dataProductKey="strategyCollection"
                />
                <QuotationsAdornment>
                    <ParentScrollTracking>
                        <PricingFaq
                            header={content?.getMoreDetailsTitle ?? undefined}
                            planDetails={
                                content?.additionalDetails ?? undefined
                            }
                        />
                    </ParentScrollTracking>
                </QuotationsAdornment>
            </ParentScrollTracking>
        </PricingPageLayout>
    );
};

const headingContainerCss = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "16px"
});
